import { useEffect, useMemo } from 'react';
import Icon from '~/components/Icon/Icon';
import Loading from '~/components/Loading/Loading';
import EditorButton from '~/components/editor/EditorButton/EditorButton';
import EditorNumber from '~/components/editor/EditorNumber/EditorNumber';
import EditorSelect from '~/components/editor/EditorSelect/EditorSelect';
import { useFetchJob } from '~/hooks/useJobs';
import { useProjects } from '~/hooks/useProjects';
import { usePCS } from '~/systems/usePCS';
import './CloudControl.css';

const USER_PORTAL_URL = import.meta.env.USER_PORTAL_URL;

export default function CloudControl() {
  const {
    cloudEmulationParams,
    currentProject,
    isSubmitDisabled,
    runOnQuantumDevice,
    setCloudEmulationParams,
    setCurrentProject,
    submissionResults,
    supportedDevices
  } = usePCS();
  const { data: projectsData, isLoading: isLoadingProjects } = useProjects();
  const jobId = useMemo(
    () => new URLSearchParams(window.location.search).get('job') ?? undefined,
    []
  );
  const { data: jobData } = useFetchJob(jobId);

  const activeProjects = useMemo(
    () =>
      (projectsData ?? []).filter(
        (project) => project.projectStatus === 'ACTIVE'
      ),
    [projectsData]
  );

  const hasActiveProjects = activeProjects.length > 0;

  // Sync current project from job or first active project
  useEffect(() => {
    const projects = activeProjects;
    const next = jobData
      ? projects.find((item) => item.id === jobData.projectId)
      : projects[0];
    setCurrentProject(next);
  }, [activeProjects, jobData, setCurrentProject]);

  const emulatorsOptions = useMemo(() => {
    const allowedDeviceTypes = currentProject?.allowedDeviceTypes ?? [];
    return supportedDevices
      .filter((device) => allowedDeviceTypes.includes(device))
      .map((device) => ({ label: device, value: device }));
  }, [currentProject?.allowedDeviceTypes, supportedDevices]);

  // Ensure selected emulator is within allowed_device_types
  useEffect(() => {
    if (emulatorsOptions.length === 0) {
      return;
    }

    const isValid = emulatorsOptions.some(
      (option) => option.value === cloudEmulationParams.emulator
    );
    if (!isValid) {
      setCloudEmulationParams((prev) => ({
        ...prev,
        emulator: emulatorsOptions[0].value
      }));
    }
  }, [
    cloudEmulationParams.emulator,
    currentProject?.allowedDeviceTypes,
    emulatorsOptions,
    setCloudEmulationParams
  ]);

  const projectOptions = useMemo(
    () => activeProjects.map(({ id, name }) => ({ label: name, value: id })),
    [activeProjects]
  );

  const lastResult = submissionResults[submissionResults.length - 1];

  if (isLoadingProjects) {
    return (
      <div className="CloudControl">
        <div className="CloudControl-body">
          <Loading />
        </div>
      </div>
    );
  }

  if (!hasActiveProjects) {
    return (
      <div className="CloudControl">
        <div className="CloudControl-body">
          <div className="CloudControl-warningAlert" role="alert">
            <Icon
              kind="warning"
              size={24}
              className="CloudControl-warningAlertIcon"
              aria-hidden
            />
            <div className="CloudControl-warningAlertContent">
              <h3 className="CloudControl-warningAlertTitle">
                No Project detected
              </h3>
              <p className="CloudControl-warningAlertBody">
                It seems you're not currently on any projects. You will need to
                be on one before being able to send your workloads to our
                cutting-edge QPU and emulators. You can create a project through
                our Cloud Platform.
              </p>
            </div>
          </div>
          <a
            className="CloudControl-warningAlertAction"
            href={`${USER_PORTAL_URL}/projects`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Create project on Pasqal Cloud
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="CloudControl">
      <div className="CloudControl-body">
        {!currentProject ? (
          <Loading />
        ) : (
          <EditorSelect
            label="Project"
            name="project"
            value={currentProject.id}
            options={projectOptions}
            onChange={(newId) => {
              setCurrentProject(projectsData?.find((p) => p.id === newId));
            }}
          />
        )}
        <div className="CloudControl-separator" />
        <EditorSelect
          label="Backend choice"
          name="backend"
          value={cloudEmulationParams.emulator}
          options={emulatorsOptions}
          onChange={(v) =>
            setCloudEmulationParams((prev) => ({ ...prev, emulator: v }))
          }
        />
        <EditorNumber
          label="Number of runs"
          name="number-of-runs"
          value={cloudEmulationParams.numberOfRuns}
          max={Infinity}
          min={1}
          onChange={(v) =>
            setCloudEmulationParams((prev) => ({
              ...prev,
              numberOfRuns: v
            }))
          }
        />
        <EditorButton
          kind="primary"
          disabled={isSubmitDisabled}
          onClick={runOnQuantumDevice}
        >
          Send to emulator
        </EditorButton>
        {lastResult?.ok && (
          <div className="CloudControl-submitResultRow">
            Batch created!
            <a
              href={`${USER_PORTAL_URL}/batches/${lastResult.val}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {lastResult.val?.substring(0, 8)}
            </a>
          </div>
        )}
        {lastResult && !lastResult.ok && (
          <div className="CloudControl-submitResultRow">
            ⚠️ Error: {lastResult.val}
          </div>
        )}
      </div>
    </div>
  );
}
